import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

// Language codes and names
const SUPPORTED_LANGUAGES = {
  // Europe
  en:    { name: "English",                  flag: "🇬🇧" },
  de:    { name: "Deutsch",                  flag: "🇩🇪" },
  fr:    { name: "Français",                 flag: "🇫🇷" },
  es:    { name: "Español",                  flag: "🇪🇸" },
  it:    { name: "Italiano",                 flag: "🇮🇹" },
  pt:    { name: "Português",                flag: "🇵🇹" },
  nl:    { name: "Nederlands",               flag: "🇳🇱" },
  pl:    { name: "Polski",                   flag: "🇵🇱" },
  ru:    { name: "Русский",                  flag: "🇷🇺" },
  uk:    { name: "Українська",               flag: "🇺🇦" },
  ro:    { name: "Română",                   flag: "🇷🇴" },
  hu:    { name: "Magyar",                   flag: "🇭🇺" },
  cs:    { name: "Čeština",                  flag: "🇨🇿" },
  sk:    { name: "Slovenčina",               flag: "🇸🇰" },
  sv:    { name: "Svenska",                  flag: "🇸🇪" },
  da:    { name: "Dansk",                    flag: "🇩🇰" },
  no:    { name: "Norsk",                    flag: "🇳🇴" },
  fi:    { name: "Suomi",                    flag: "🇫🇮" },
  bg:    { name: "Български",                flag: "🇧🇬" },
  hr:    { name: "Hrvatski",                 flag: "🇭🇷" },
  sr:    { name: "Српски",                   flag: "🇷🇸" },
  sl:    { name: "Slovenščina",              flag: "🇸🇮" },
  lt:    { name: "Lietuvių",                 flag: "🇱🇹" },
  lv:    { name: "Latviešu",                 flag: "🇱🇻" },
  et:    { name: "Eesti",                    flag: "🇪🇪" },
  el:    { name: "Ελληνικά",                 flag: "🇬🇷" },
  tr:    { name: "Türkçe",                   flag: "🇹🇷" },
  ca:    { name: "Català",                   flag: "🏴" },
  gl:    { name: "Galego",                   flag: "🏴" },
  eu:    { name: "Euskara",                  flag: "🏴" },
  cy:    { name: "Cymraeg",                  flag: "🏴󠁧󠁢󠁷󠁬󠁳󠁿" },
  ga:    { name: "Gaeilge",                  flag: "🇮🇪" },
  mt:    { name: "Malti",                    flag: "🇲🇹" },
  // Asia Pacific
  zh:    { name: "中文 (简体)",               flag: "🇨🇳" },
  "zh-TW": { name: "中文 (繁體)",             flag: "🇹🇼" },
  ja:    { name: "日本語",                    flag: "🇯🇵" },
  ko:    { name: "한국어",                    flag: "🇰🇷" },
  th:    { name: "ภาษาไทย",                  flag: "🇹🇭" },
  vi:    { name: "Tiếng Việt",               flag: "🇻🇳" },
  id:    { name: "Bahasa Indonesia",         flag: "🇮🇩" },
  ms:    { name: "Bahasa Melayu",            flag: "🇲🇾" },
  tl:    { name: "Filipino",                 flag: "🇵🇭" },
  km:    { name: "ភាសាខ្មែរ",                flag: "🇰🇭" },
  lo:    { name: "ພາສາລາວ",                  flag: "🇱🇦" },
  my:    { name: "မြန်မာဘာသာ",               flag: "🇲🇲" },
  si:    { name: "සිංහල",                    flag: "🇱🇰" },
  // South Asia
  hi:    { name: "हिन्दी",                   flag: "🇮🇳" },
  bn:    { name: "বাংলা",                    flag: "🇧🇩" },
  ur:    { name: "اردو",                     flag: "🇵🇰" },
  pa:    { name: "ਪੰਜਾਬੀ",                   flag: "🇮🇳" },
  gu:    { name: "ગુજરાતી",                  flag: "🇮🇳" },
  mr:    { name: "मराठी",                    flag: "🇮🇳" },
  ta:    { name: "தமிழ்",                    flag: "🇮🇳" },
  te:    { name: "తెలుగు",                   flag: "🇮🇳" },
  kn:    { name: "ಕನ್ನಡ",                    flag: "🇮🇳" },
  ml:    { name: "മലയാളം",                   flag: "🇮🇳" },
  ne:    { name: "नेपाली",                   flag: "🇳🇵" },
  // Middle East
  ar:    { name: "العربية",                  flag: "🇸🇦" },
  fa:    { name: "فارسی",                    flag: "🇮🇷" },
  he:    { name: "עברית",                    flag: "🇮🇱" },
  // Americas
  "pt-BR": { name: "Português (Brasil)",    flag: "🇧🇷" },
  ht:    { name: "Kreyòl ayisyen",           flag: "🇭🇹" },
  // Africa
  sw:    { name: "Kiswahili",                flag: "🇰🇪" },
  am:    { name: "አማርኛ",                    flag: "🇪🇹" },
  so:    { name: "Soomaali",                 flag: "🇸🇴" },
  zu:    { name: "isiZulu",                  flag: "🇿🇦" },
  af:    { name: "Afrikaans",                flag: "🇿🇦" },
};

const STORAGE_KEY = 'nexuscms_language_preference';

/**
 * Language Selector Component
 * Allows users to select their preferred language for page translations.
 * Remembers preference in localStorage.
 */
const LanguageSelector = ({ 
  onLanguageChange, 
  currentLanguage = 'en',
  variant = 'default', // 'default', 'compact', 'minimal'
  enabledLanguages = null, // Array of enabled language codes, null for all
  className = ''
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [isLoading, setIsLoading] = useState(false);

  // Load saved preference on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES[saved]) {
      setSelectedLanguage(saved);
      if (onLanguageChange && saved !== currentLanguage) {
        onLanguageChange(saved);
      }
    }
  }, []);

  // Filter languages if specific ones are enabled
  const availableLanguages = enabledLanguages 
    ? Object.entries(SUPPORTED_LANGUAGES).filter(([code]) => enabledLanguages.includes(code))
    : Object.entries(SUPPORTED_LANGUAGES);

  const handleLanguageSelect = async (langCode) => {
    if (langCode === selectedLanguage) return;
    
    setIsLoading(true);
    setSelectedLanguage(langCode);
    
    // Save preference
    localStorage.setItem(STORAGE_KEY, langCode);
    
    // Notify parent
    if (onLanguageChange) {
      await onLanguageChange(langCode);
    }
    
    setIsLoading(false);
  };

  const currentLang = SUPPORTED_LANGUAGES[selectedLanguage] || SUPPORTED_LANGUAGES.en;

  // Minimal variant - just flag icon
  if (variant === 'minimal') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`h-8 w-8 p-0 ${className}`}
            disabled={isLoading}
          >
            <span className="text-lg">{currentLang.flag}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[300px] overflow-y-auto">
          {availableLanguages.map(([code, lang]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageSelect(code)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
              {code === selectedLanguage && <Check size={14} className="ml-auto text-green-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Compact variant - flag + code
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={`h-8 gap-1.5 ${className}`}
            disabled={isLoading}
          >
            <span>{currentLang.flag}</span>
            <span className="uppercase text-xs font-medium">{selectedLanguage}</span>
            <ChevronDown size={12} className="opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[300px] overflow-y-auto">
          {availableLanguages.map(([code, lang]) => (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageSelect(code)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
              {code === selectedLanguage && <Check size={14} className="ml-auto text-green-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant - full display
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`h-9 gap-2 ${className}`}
          disabled={isLoading}
        >
          <Globe size={16} className="opacity-70" />
          <span>{currentLang.flag}</span>
          <span className="hidden sm:inline">{currentLang.name}</span>
          <ChevronDown size={14} className="opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 max-h-[400px] overflow-y-auto">
        {availableLanguages.map(([code, lang]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleLanguageSelect(code)}
            className={`flex items-center gap-2 cursor-pointer ${
              code === selectedLanguage ? 'bg-indigo-50' : ''
            }`}
          >
            <span className="text-base">{lang.flag}</span>
            <span className="flex-1">{lang.name}</span>
            {code === selectedLanguage && (
              <Check size={14} className="text-indigo-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Hook for using translation in components
 */
export const useTranslation = (defaultLanguage = 'en') => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && SUPPORTED_LANGUAGES[saved] ? saved : defaultLanguage;
  });
  const [isTranslating, setIsTranslating] = useState(false);

  const changeLanguage = async (newLang) => {
    if (!SUPPORTED_LANGUAGES[newLang]) return;
    
    localStorage.setItem(STORAGE_KEY, newLang);
    setLanguage(newLang);
  };

  return {
    language,
    changeLanguage,
    isTranslating,
    setIsTranslating,
    languageInfo: SUPPORTED_LANGUAGES[language],
    supportedLanguages: SUPPORTED_LANGUAGES
  };
};

/**
 * Get user's preferred language (from storage or browser)
 */
export const getPreferredLanguage = () => {
  // Check localStorage first
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED_LANGUAGES[saved]) {
    return saved;
  }
  
  // Fall back to browser language
  const browserLang = navigator.language?.split('-')[0];
  if (browserLang && SUPPORTED_LANGUAGES[browserLang]) {
    return browserLang;
  }
  
  return 'en';
};

export { SUPPORTED_LANGUAGES };
export default LanguageSelector;
